import React, { useMemo, type FC } from 'react';
import type { TimeSlot } from '../../../../state/infiniteCalendarSeries';
import ConnectedTimeSlotSelector from '../TimeSlotSelector/ConnectedTimeSlotSelector';
import {
    getSessionsByDurationAndTimeOfDay,
    getSortedSessionsByDuration,
    shouldShowDurationTag,
    sortSessionsByStartTime,
} from '../utils';

type SimpleTimeSlotProps = {
    sessions: TimeSlot[];
    timezone: string;
    locale: string;
    isAppRebrandActive: boolean;
    onSessionClick: (sessionId: string, sessionLabel: string) => void;
};

const SimpleTimeSlotList: FC<SimpleTimeSlotProps> = ({
    timezone,
    locale,
    sessions = [],
    isAppRebrandActive,
    onSessionClick,
}) => {
    const groupedSessions = useMemo(() => {
        const { sessionsByDuration } = getSessionsByDurationAndTimeOfDay({
            timeSlots: sessions,
            timezone,
            locale,
            isAppRebrandActive,
        });
        const allSessions = Object.values(sessionsByDuration).flat();
        const shouldShowDuration = shouldShowDurationTag(allSessions);
        const sortedGroupedSessions = shouldShowDuration
            ? getSortedSessionsByDuration(sessionsByDuration)
            : [
                  {
                      duration: undefined,
                      sessions: sortSessionsByStartTime(allSessions),
                  },
              ];
        return sortedGroupedSessions;
    }, [sessions, timezone, locale, isAppRebrandActive]);

    return (
        <div data-testid="simple-time-slot-list">
            {groupedSessions.map(({ duration, sessions }, index) => (
                <ConnectedTimeSlotSelector
                    key={index}
                    sessions={sessions}
                    duration={duration ? String(duration) : undefined}
                    onSessionClick={onSessionClick}
                    isAppRebrandActive={isAppRebrandActive}
                />
            ))}
        </div>
    );
};

export default SimpleTimeSlotList;
